/**
 * What kind of failure took a page down, as far as the error itself can say.
 *
 * A reader shown "INTERNAL ASSERTION FAILED: Unexpected state" learns nothing,
 * and neither does whoever debugs it: the words name the SDK and hide the
 * cause (Jira NAV-169). Two classes are worth telling apart from the rest,
 * because each asks something different of the reader.
 *
 * - `read-refused`: the security rules refused a read. Expected, designed for,
 *   and normally answered by a hook's error callback; reaching a boundary means
 *   a render or an effect threw it instead. Another page may work fine.
 * - `firestore-internal`: the Firestore SDK failed one of its own assertions.
 *   Its async queue is dead after one, so every later read and write in the tab
 *   throws too, and only a reload recovers. A refused listen on a re-added
 *   target is the known trigger (firebase-js-sdk #9267, fixed in
 *   `@firebase/firestore` 4.14.1), but the assertion has had several causes, so
 *   the page must not claim which one.
 */
export type CrashKind = "read-refused" | "firestore-internal" | "unknown";

/** The heading and sentence for each kind of crash, keyed in the `error`
 *  bundle. One table for both boundaries, so a refused read reads the same
 *  whichever one caught it. */
export const CRASH_COPY: Record<CrashKind, { title: string; message: string }> =
  {
    "read-refused": {
      title: "error:readRefused.title",
      message: "error:readRefused.message",
    },
    "firestore-internal": {
      title: "error:connectionLost.title",
      message: "error:connectionLost.message",
    },
    unknown: {
      title: "error:fallback.title",
      message: "error:fallback.message",
    },
  };

const FIRESTORE_ASSERTION = /FIRESTORE \([^)]*\) INTERNAL ASSERTION FAILED/;

export function crashKind(error: unknown): CrashKind {
  if (typeof error !== "object" || error === null) return "unknown";
  const { code, message } = error as { code?: unknown; message?: unknown };
  const text = typeof message === "string" ? message : "";
  // Checked first: an assertion's message can quote the refusal that preceded
  // it, and a dead SDK needs a reload whatever set it off.
  if (FIRESTORE_ASSERTION.test(text)) return "firestore-internal";
  if (code === "permission-denied") return "read-refused";
  return "unknown";
}
